import { ColumnDef } from "@tanstack/react-table";
import {
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Button } from "../../button";
import { ArrowUpDown } from "lucide-react";
import TableWrapper from "../TableWrapper";
import { useTranslation } from "react-i18next";
import toast, { Toaster } from "react-hot-toast";
import {
  BLACKLIST_STATUS,
  BlacklistingReport,
  DOC_TYPE_ID,
  EXPORT_TYPES,
  REPORT_NAMES,
  TABLE_IDS,
  tableColumnsAr,
  tableColumnsEn,
} from "../../../constants/common";
import ApprovalStatusesModal from "../../modal/ApprovalStatusesModal";
import {
  downloadFile,
  formattedDateTimeWithoutSec,
} from "../../../lib/functions";
import { ExportBlacklistingReportForExcelPDFBody } from "../../../hooks/report/interface";
import {
  useExportBlacklistingReportForExcel,
  useExportBlacklistingReportForPDF,
} from "../../../hooks/report/mutation";
import StatusCodes from "../../../constants/StatusCodes";
import { cn } from "../../../utils/cn";

interface ExternalUser {
  s_FullName: string;
  s_CompanyName: string;
  s_Email: string;
  s_MobileNumber: string;
  s_UserName: string;
  dt_CreatedDate: string;
  exportBody: ExportBlacklistingReportForExcelPDFBody;
}

interface BlacklistingReportTableProps {
  allBlacklisting: ExternalUser[];
  page: number;
  pages: number;
  setPage: Dispatch<SetStateAction<number>>;
  count: number;
  exportBody?: ExportBlacklistingReportForExcelPDFBody;
  pageSize: number;
  setPageSize: Dispatch<SetStateAction<number>>;
}

const BlacklistingReportTable = ({
  allBlacklisting: allExternalUsers,
  pages,
  page,
  setPage,
  count,
  exportBody,
  pageSize,
  setPageSize,
}: BlacklistingReportTableProps) => {
  const { t, i18n } = useTranslation();

  const [isApprovalStatusesModalOpen, setIsApprovalStatusesModalOpen] =
    useState(false);
  const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[]>([]);

  const { mutateAsync: ExportBlacklistingReportForExcel } =
    useExportBlacklistingReportForExcel();

  const { mutateAsync: ExportBlacklistingReportForPDF } =
    useExportBlacklistingReportForPDF();

  const columns: ColumnDef<BlacklistingReport>[] = useMemo(
    () => [
      {
        accessorKey: "idType",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("idType")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          if (!row.original.idType) return;
          return (
            <span>
              {row.original.idType === DOC_TYPE_ID.NATIONAL_ID
                ? t("nationalID")
                : row.original.idType === DOC_TYPE_ID.PASSPORT
                ? t("passport")
                : row.original.idType === DOC_TYPE_ID.IQAMA
                ? t("iqama")
                : row.original.idType === DOC_TYPE_ID.VISA
                ? t("visa")
                : ""}
            </span>
          );
        },
      },
      {
        accessorKey: "s_EmiratesID",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("nationalID")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return row.original.idType === DOC_TYPE_ID.NATIONAL_ID ? (
            <span className="capitalize">{row.original.s_EmiratesID}</span>
          ) : null;
        },
      },
      {
        accessorKey: "s_PassportNumber",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("passport")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return row.original.idType === DOC_TYPE_ID.PASSPORT ? (
            <span className="capitalize">{row.original.s_PassportNumber}</span>
          ) : null;
        },
      },
      {
        accessorKey: "s_VisaNo",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("visa")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return row.original.idType === DOC_TYPE_ID.VISA ? (
            <span className="capitalize">{row.original.s_VisaNo}</span>
          ) : null;
        },
      },
      {
        accessorKey: "s_Iqama",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("iqama")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return row.original.idType === DOC_TYPE_ID.IQAMA ? (
            <span className="capitalize">{row.original.s_Iqama}</span>
          ) : null;
        },
      },
      {
        accessorKey: "s_Reason",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("reason")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
      },
      {
        accessorKey: "s_BlackListedStatus_En",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("status")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          if (!row.original.n_BlackListedStatus) return;

          return (
            <div
              className={cn(
                "text-center rounded-lg py-0.5 px-2 w-max",
                row.original.n_BlackListedStatus ===
                  BLACKLIST_STATUS.BLACKLISTED
                  ? "bg-red-600"
                  : "bg-green-600"
              )}
            >
              <p className="text-white">
                {i18n.language === "en"
                  ? row.original.s_BlackListedStatus_En
                  : row.original.s_BlackListedStatus_Ar}
              </p>
            </div>
          );
        },
      },
      {
        accessorKey: "s_ActionBy",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("actionBy")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return <span>{row.original.s_ActionBy}</span>;
        },
      },
      {
        accessorKey: "dt_ActionDateTime",
        header: ({ column }) => (
          <Button
            className="flex w-full justify-between text-md"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("actionDate")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          return (
            <span>
              {formattedDateTimeWithoutSec(row.original.dt_ActionDateTime)}
            </span>
          );
        },
      },
    ],
    [i18n.language, t]
  );

  const onExport = useCallback(
    async (type: EXPORT_TYPES) => {
      if (!exportBody) return;

      if (visibleColumnKeys.length === 0) {
        toast.error(t("noColumnSelectedForExport"));
        return;
      }

      try {
        const body = {
          ...exportBody,
          ExportOptions: visibleColumnKeys.map((key) => ({
            ColumnKey: key,
            ColumnNameEn: tableColumnsEn[key],
            ColumnNameAr: tableColumnsAr[key],
            CurrentLanguage: i18n.language,
          })),
        };

        let res;
        if (type === EXPORT_TYPES.EXCEL) {
          res = await ExportBlacklistingReportForExcel(body);
        } else {
          res = await ExportBlacklistingReportForPDF(body);
        }

        downloadFile(res, REPORT_NAMES.EXTERNAL_USERS_REPORT);
        toast.success(t("exportSuccess"));
      } catch (error: any) {
        if (error.statusCode === StatusCodes.NOT_FOUND) {
          toast.error(t("noDataToExport"));
        } else {
          toast.error(t("somethingWentWrong"));
        }
      }
    },
    [
      ExportBlacklistingReportForExcel,
      ExportBlacklistingReportForPDF,
      exportBody,
      i18n.language,
      t,
      visibleColumnKeys,
    ]
  );

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <TableWrapper
        data={allExternalUsers}
        columns={columns}
        page={page}
        setPage={setPage}
        totalPages={pages}
        count={count}
        isExportExcel
        onExport={onExport}
        isSearch={false}
        tableId={TABLE_IDS.BLACKLISTING_REPORT}
        setVisibleColumnKeys={setVisibleColumnKeys}
        pageSize={pageSize}
        setPageSize={setPageSize}
      />

      <ApprovalStatusesModal
        isOpen={isApprovalStatusesModalOpen}
        onOpenChange={() => setIsApprovalStatusesModalOpen(false)}
      />
    </>
  );
};

export default memo(BlacklistingReportTable);
